import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const QUICK_LINK_KEYS = ['About Us', 'Our Fleet', 'Services', 'Schedules', 'Amenities', 'Contact'];
const LINK_HREFS: Record<string, string> = {
  'About Us': '#about',
  'Our Fleet': '/fleet',
  'Services': '#services',
  'Schedules': '/schedules',
  'Amenities': '/amenities',
  'Contact': '#contact',
};
const LINK_LABEL_KEYS: Record<string, string> = {
  'About Us': 'nav_about',
  'Our Fleet': 'nav_fleet',
  'Services': 'nav_services',
  'Schedules': 'nav_schedules',
  'Amenities': 'nav_amenities',
  'Contact': 'nav_contact',
};

export default function Footer() {
  const { t } = useTranslation();
  const location = useLocation();

  const handleNav = (href: string) => {
    if (href.startsWith('/')) {
      window.REACT_APP_NAVIGATE?.(href);
      return;
    }
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE?.('/');
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }, 350);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="w-full bg-[#111111] pt-16 pb-8">
      {/* Gold top line */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent mb-14" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          {/* Column 1: Brand */}
          <div>
            <img
              src="https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/d8bdbfe7-b9ea-40a1-a426-22f1e1a4a677_12.png?v=1abd8e4dc9705f9939d4c05b25cb73fa"
              alt="Phoenix Transportation"
              className="h-24 w-auto object-contain mb-5"
            />
            <p className="text-white/40 text-sm leading-relaxed mb-4">{t('footer_tagline')}</p>
            <p className="text-[#C9A84C]/60 text-xs italic font-playfair">{t('footer_motto')}</p>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-[#C9A84C] text-xs font-bold tracking-[0.25em] uppercase mb-5">{t('footer_quickLinks')}</h4>
            <ul className="space-y-3">
              {QUICK_LINK_KEYS.map((key) => (
                <li key={key}>
                  <a
                    href={LINK_HREFS[key]}
                    onClick={(e) => { e.preventDefault(); handleNav(LINK_HREFS[key]); }}
                    className="text-white/50 hover:text-[#C9A84C] text-sm transition-colors duration-200 cursor-pointer"
                  >
                    {t(LINK_LABEL_KEYS[key])}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Routes */}
          <div>
            <h4 className="text-[#C9A84C] text-xs font-bold tracking-[0.25em] uppercase mb-5">{t('footer_routes')}</h4>
            <ul className="space-y-3 text-sm text-white/50">
              <li>{t('footer_route1')}</li>
              <li>{t('footer_route2')}</li>
              <li className="pt-2 border-t border-white/10">
                <span className="text-white/30 text-xs">{t('footer_departure_label')}</span>
              </li>
              <li>{t('footer_departure_val')}</li>
              <li>
                <span className="text-white/30 text-xs">{t('footer_journey_label')}</span>
              </li>
              <li>{t('footer_journey_val')}</li>
            </ul>
          </div>

          {/* Column 4: Contact & Social */}
          <div>
            <h4 className="text-[#C9A84C] text-xs font-bold tracking-[0.25em] uppercase mb-5">{t('footer_contact')}</h4>
            <ul className="space-y-3 text-sm text-white/50">
              <li className="flex items-center gap-2">
                <i className="ri-phone-line text-[#C9A84C] text-base" />
                +507 6000-0000
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-[#C9A84C] text-base" />
                info@phoenixtransportation.pa
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-map-pin-2-line text-[#C9A84C] text-base shrink-0 mt-0.5" />
                Panama City &amp; Chitré, Panama
              </li>
            </ul>

            {/* Social Icons */}
            <div className="mt-6 flex items-center gap-3">
              <a href="https://facebook.com" rel="nofollow" target="_blank" className="w-9 h-9 flex items-center justify-center border border-white/10 rounded-full text-white/40 hover:text-[#C9A84C] hover:border-[#C9A84C]/50 transition-all duration-300 cursor-pointer">
                <i className="ri-facebook-fill" />
              </a>
              <a href="https://instagram.com" rel="nofollow" target="_blank" className="w-9 h-9 flex items-center justify-center border border-white/10 rounded-full text-white/40 hover:text-[#C9A84C] hover:border-[#C9A84C]/50 transition-all duration-300 cursor-pointer">
                <i className="ri-instagram-line" />
              </a>
              <a href="https://wa.me/50760000000" rel="nofollow" target="_blank" className="w-9 h-9 flex items-center justify-center border border-white/10 rounded-full text-white/40 hover:text-[#C9A84C] hover:border-[#C9A84C]/50 transition-all duration-300 cursor-pointer">
                <i className="ri-whatsapp-line" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} Phoenix Transportation. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-white/20 text-xs">
            <a href="#" className="hover:text-[#C9A84C] transition-colors duration-200">{t('footer_privacy')}</a>
            <span>·</span>
            <a href="#" className="hover:text-[#C9A84C] transition-colors duration-200">{t('footer_terms')}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
